import React from 'react';
import Highcharts from 'highcharts/highstock';
import HighchartsReact from 'highcharts-react-official';
import { useTranslation } from 'react-i18next';
import { Box } from '@material-ui/core';
import { Alert } from '@mui/material';
import { GraphResponseTimeFrecuencyInfo } from './GraphResponseTimeFrecuencyInfo';
import { useObjectivesList } from '../../../hooks/useObjectivesList';

export function CumulativeFrecuency({ externalData }) {
  const { t } = useTranslation();
  const chartRef = React.useRef(null);
  const containerRef = React.useRef(null);
  const [hoverData, setHoverData] = React.useState(null);
  const { data: objectives } = useObjectivesList({ fromStorage: true });

  React.useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const resizeObserver = new ResizeObserver(() => {
      if (chartRef.current) {
        chartRef.current.chart.reflow();
      }
    });

    resizeObserver.observe(container);

    return () => {
      resizeObserver.unobserve(container);
    };
  }, []);

  function onHoverData(e) {
    const { x, y, series } = e;
    let point = [];
    point['x'] = x;
    point['y'] = y;
    let step = series.userOptions.name;
    setHoverData({ ...point, step });
  }

  const processData = () => {
    let arrayChartData = [];
    let lastEventDict = [];
    const colors = [
      '#2199e8',
      '#fed12d',
      '#ff7373',
      '#ccff00',
      '#b6fcd5',
      '#065535',
      '#ffc0cb',
      '#008080',
      '#00ffff',
      '#ffa500',
      '#800000',
      '#fff68f',
      '#bada55',
      '#8a2be2',
      '#3b5998',
    ];

    let sla_rendimiento_ok = 90;
    let sla_rendimiento_error = 0;

    if (objectives && externalData.Objetivo?.Objetivo_Id) {
      const currentObj = objectives.find(
        (obj) =>
          parseInt(obj.objetivo_id) ===
          parseInt(externalData.Objetivo.Objetivo_Id)
      );
      if (currentObj) {
        sla_rendimiento_error = parseFloat(currentObj.sla_rendimiento_error);
        sla_rendimiento_ok = parseFloat(currentObj.sla_rendimiento_ok);
      }
    }

    Object.keys(externalData.Objetivo?.Paso ?? {}).map((key) => {
      let chartDataSeries = {};
      let arrayData = [];
      var cumulativeElements = 0;
      let rowLen = externalData.Objetivo.Paso[key].rendimientoDetalle.length;

      externalData.Objetivo.Paso[key].rendimientoDetalle.forEach(
        (item, forEachKey) => {
          var dict = {};
          cumulativeElements = cumulativeElements + item.elementos;
          dict['y'] = cumulativeElements;
          dict['x'] = item.respuesta;
          arrayData.push(dict);
          if (forEachKey == rowLen - 1 && key == 0) {
            lastEventDict['x'] = dict['y'];
            lastEventDict['y'] = dict['x'];
            lastEventDict['name'] = externalData.Objetivo.Paso[key].paso_nombre;
          }
        }
      );
      chartDataSeries['name'] = externalData.Objetivo.Paso[key].paso_nombre;
      chartDataSeries['color'] = colors[key];
      chartDataSeries['data'] = arrayData;
      arrayChartData.push(chartDataSeries);
    });

    return {
      colors: [
        '#6AF9C4',
        '#FFF263',
        '#FF9655',
        '#64E572',
        '#0000FF',
        '#FFFF00',
        '#0A2A12',
        '#FF0000',
      ],
      chart: {
        type: 'spline',
        zoomType: 'x',
        ignoreHiddenSeries: false,
        marginRight: 30,
        resetZoomButton: {
          theme: {
            fill: 'white',
            stroke: 'silver',
            r: 0,
            states: {
              hover: {
                fill: '#41739D',
                style: {
                  color: 'white',
                },
              },
            },
          },
        },
      },
      title: {
        text: '',
        style: {
          fontSize: '',
        },
      },
      subtitle: {
        text: '',
        style: {
          color: '',
          fontWeight: '',
        },
      },
      xAxis: {
        min: 0,
        title: {
          text: t('cumulative-frequency.graph.xAxis.title.text'),
        },
        labels: {
          useHTML: true,
        },
        plotLines: [
          {
            value: sla_rendimiento_ok,
            color: '#54a51c',
            width: 2,
            zIndex: 3,
          },
          {
            value: sla_rendimiento_error,
            color: '#D22129',
            width: 2,
            zIndex: 3,
          },
        ],
      },
      yAxis: {
        min: 0,
        title: {
          text: t('cumulative-frequency.graph.yAxis.title.text'),
        },
        plotLines: [],
      },
      setOptions: {
        time: {
          useUTC: false,
        },
        lang: {
          months: t('months', { returnObjects: true }),
          weekdays: t('weekDays', { returnObjects: true }),
        },
      },
      plotOptions: {
        series: {
          turboThreshold: 20000,
          marker: {
            symbol: 'circle',
            width: 8,
            height: 8,
            enabled: false,
          },
          point: {
            events: {
              mouseOver: function (e) {
                onHoverData(this);
              },
            },
          },
        },
      },
      legend: {
        enabled: true,
        useHTML: true,
        symbolWidth: 0,
        labelFormatter: function () {
          let symbol =
            '<div style="background:' +
            this.color +
            ';width:15px;height:3px;margin-top:5px"></div>';
          let name =
            '<button style="padding-left:4px;border-width:0px;background:white">' +
            this.name +
            '</button>';
          return (
            '<div style="display: flex;flex-direction: row;flex-wrap: nowrap;justify-content: space-between;">' +
            symbol +
            ' ' +
            name +
            '</div>'
          );
        },
      },
      tooltip: {
        enabled: true,
      },
      credits: {
        enabled: false,
      },
      series: arrayChartData,
    };
  };

  if (!externalData) {
    return <Alert severity="info">{t('text.no-data')}</Alert>;
  }

  const chartOptions = processData();

  return (
    <Box ref={containerRef} width="100%">
      <HighchartsReact
        highcharts={Highcharts}
        options={chartOptions}
        ref={chartRef}
        containerProps={{ style: { width: '100%' } }}
      />
      <GraphResponseTimeFrecuencyInfo {...hoverData} />
    </Box>
  );
}
